import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/** Valor de `product-grid-view` de la vista ampliada (3 columnas en escritorio). */
const LARGE_VIEW = 'large';

/**
 * `sizes` de las fotos de las tarjetas en la vista ampliada. El que pone el tema se calcula para la
 * rejilla por defecto (mas columnas): con 3 columnas las fotos se pedirian demasiado pequeñas y se verian borrosas.
 */
const LARGE_VIEW_SIZES = '(min-width: 750px) 34vw, 100vw';

/**
 * El `sizes` que puso el tema a cada foto, para devolverlo al salir de la vista ampliada.
 * @type {WeakMap<HTMLImageElement, string>}
 */
const defaultImageSizes = new WeakMap();

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** Si alguna foto lleva ahora el `sizes` de la vista ampliada. */
  #imageSizesChanged = false;

  /**
   * Las tarjetas nuevas (scroll infinito, filtros, orden) llegan con el `sizes` por defecto:
   * se reajustan en cuanto aparecen.
   */
  #imageSizesObserver = new MutationObserver(() => this.#syncImageSizes());

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#imageSizesObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['sizes'],
    });
    this.#syncImageSizes();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#imageSizesObserver.disconnect();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#syncImageSizes();
  }

  /**
   * Ajusta el `sizes` de las fotos de las tarjetas a la vista actual: en la ampliada pide el tamano
   * de 3 columnas y al salir de ella devuelve el original.
   */
  #syncImageSizes() {
    const { grid } = this.refs;
    if (!grid) return;

    const isLarge = grid.getAttribute('product-grid-view') === LARGE_VIEW;
    if (!isLarge && !this.#imageSizesChanged) return;

    for (const image of grid.querySelectorAll('img.product-media__image')) {
      if (isLarge) {
        const original = defaultImageSizes.get(image) ?? image.getAttribute('sizes') ?? '';
        defaultImageSizes.set(image, original);

        // Las fotos con carga diferida llevan `auto`: se conserva para que midan por su sitio real.
        const sizes = original.startsWith('auto') ? `auto, ${LARGE_VIEW_SIZES}` : LARGE_VIEW_SIZES;
        if (image.getAttribute('sizes') !== sizes) image.setAttribute('sizes', sizes);
      } else if (defaultImageSizes.has(image)) {
        image.setAttribute('sizes', defaultImageSizes.get(image) ?? '');
        defaultImageSizes.delete(image);
      }
    }

    this.#imageSizesChanged = isLarge;
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
